// Surgical edits to an .xlsx package.
//
// Rewriting a workbook through a library means re-emitting every part, and any
// feature the library does not model comes back subtly wrong - Excel then offers
// to "repair" the file on open. This module instead opens the .xlsx as the zip it
// is, edits only the cells asked for inside one worksheet's XML, and leaves every
// other byte of the package exactly as Excel wrote it.

import JSZip from "jszip";

const CELL_REFERENCE = /^([A-Z]+)(\d+)$/;

/** "J" -> 10, "AA" -> 27 */
export const columnNumber = (letters) =>
  letters.split("").reduce((total, letter) => total * 26 + (letter.charCodeAt(0) - 64), 0);

/** 10 -> "J" */
export const columnLetters = (index) => {
  let rest = index;
  let letters = "";
  while (rest > 0) {
    const remainder = (rest - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    rest = Math.floor((rest - remainder) / 26);
  }
  return letters;
};

const escapeXml = (text) =>
  String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Find <row r="N">...</row>, or the self-closing form. */
const rowPattern = (rowNumber) =>
  new RegExp(
    '<row\\b[^>]*\\br="' +
      rowNumber +
      '"[^>]*\\/>|<row\\b[^>]*\\br="' +
      rowNumber +
      '"[^>]*>[\\s\\S]*?<\\/row>'
  );

/** Find <c r="J4" ...>...</c>, or the self-closing form. */
const cellPattern = (ref) =>
  new RegExp(
    '<c\\b[^>]*\\br="' + ref + '"[^>]*\\/>|<c\\b[^>]*\\br="' + ref + '"[^>]*>[\\s\\S]*?<\\/c>'
  );

/**
 * Build the replacement <c> element.
 * Numbers go in as numbers so Excel does not flag "number stored as text";
 * text uses an inline string so the shared string table is never touched.
 */
const buildCell = (ref, value, styleAttribute) => {
  const style = styleAttribute ? " " + styleAttribute : "";

  if (value === null || value === undefined || value === "") {
    return '<c r="' + ref + '"' + style + "/>";
  }
  if (typeof value === "number") {
    return '<c r="' + ref + '"' + style + "><v>" + value + "</v></c>";
  }
  return (
    '<c r="' +
    ref +
    '"' +
    style +
    ' t="inlineStr"><is><t xml:space="preserve">' +
    escapeXml(value) +
    "</t></is></c>"
  );
};

/** Keep the existing cell's style so formatting and fills survive. */
const styleOf = (cellXml) => {
  const match = cellXml && cellXml.match(/\bs="\d+"/);
  return match ? match[0] : "";
};

/** Put a cell into a row's XML, in column order. */
const insertCell = (rowXml, cellXml, targetColumn) => {
  const openMatch = rowXml.match(/^<row\b[^>]*?(\/?)>/);
  if (!openMatch) return rowXml;

  // An empty self-closing row has to become a container first
  if (openMatch[1] === "/") {
    return openMatch[0].slice(0, -2) + ">" + cellXml + "</row>";
  }

  const opening = openMatch[0];
  const inner = rowXml.slice(opening.length, rowXml.lastIndexOf("</row>"));

  const siblings = inner.match(/<c\b[^>]*\/>|<c\b[^>]*>[\s\S]*?<\/c>/g) || [];
  const following = siblings.find((sibling) => {
    const refMatch = sibling.match(/\br="([A-Z]+)\d+"/);
    return refMatch && columnNumber(refMatch[1]) > targetColumn;
  });

  const updated = following ? inner.replace(following, cellXml + following) : inner + cellXml;
  return opening + updated + "</row>";
};

/**
 * Apply cell edits to one worksheet's XML.
 * @param {string} sheetXml
 * @param {Array<{ref: string, value: string|number|null}>} edits
 * @returns {{xml: string, applied: number, skipped: string[]}}
 */
export const applyCellEdits = (sheetXml, edits = []) => {
  let xml = sheetXml;
  let applied = 0;
  const skipped = [];
  let widestColumn = 0;

  edits.forEach((edit) => {
    const parts = String(edit.ref).match(CELL_REFERENCE);
    if (!parts) {
      skipped.push(edit.ref);
      return;
    }

    const targetColumn = columnNumber(parts[1]);
    const rowMatch = xml.match(rowPattern(Number(parts[2])));

    if (!rowMatch) {
      skipped.push(edit.ref); // the row is not in the sheet, so there is nothing to change
      return;
    }

    const rowXml = rowMatch[0];
    const existing = rowXml.match(cellPattern(edit.ref));
    const replacement = buildCell(edit.ref, edit.value, styleOf(existing && existing[0]));

    const updatedRow = existing
      ? rowXml.replace(existing[0], replacement)
      : insertCell(rowXml, replacement, targetColumn);

    xml = xml.replace(rowXml, updatedRow);
    applied++;
    widestColumn = Math.max(widestColumn, targetColumn);
  });

  // <dimension> is advisory, but keep it honest if we wrote past its right edge
  xml = xml.replace(
    /<dimension ref="([A-Z]+)(\d+):([A-Z]+)(\d+)"\s*\/>/,
    (whole, firstCol, firstRow, lastCol, lastRow) =>
      columnNumber(lastCol) >= widestColumn
        ? whole
        : '<dimension ref="' +
          firstCol +
          firstRow +
          ":" +
          columnLetters(widestColumn) +
          lastRow +
          '"/>'
  );

  return { xml, applied, skipped };
};

/** Work out which worksheet part the app reads, mirroring the parser's choice. */
export const findSheetPath = async (zip) => {
  const workbookXml = await zip.file("xl/workbook.xml").async("string");
  const relsXml = await zip.file("xl/_rels/workbook.xml.rels").async("string");

  const sheets = (workbookXml.match(/<sheet\b[^>]*\/>/g) || []).map((entry) => ({
    name: (entry.match(/\bname="([^"]*)"/) || [])[1] || "",
    relationshipId: (entry.match(/\br:id="([^"]*)"/) || [])[1] || "",
  }));
  if (sheets.length === 0) throw new Error("No worksheet found in the workbook");

  const chosen = sheets.find((sheet) => sheet.name === "RoomAllocation") || sheets[0];

  const relationship = (relsXml.match(/<Relationship\b[^>]*\/>/g) || []).find(
    (entry) => (entry.match(/\bId="([^"]*)"/) || [])[1] === chosen.relationshipId
  );
  const target = relationship && (relationship.match(/\bTarget="([^"]*)"/) || [])[1];
  if (!target) throw new Error('Could not locate the sheet "' + chosen.name + '" in the workbook');

  const path = target.replace(/^\/?(xl\/)?/, "xl/");
  if (!zip.file(path)) throw new Error("Worksheet part " + path + " is missing from the workbook");

  return { path, name: chosen.name };
};

/**
 * Edit cells in place and return the repacked workbook.
 * Every part other than the one worksheet is left untouched.
 *
 * @param {File|Blob} file - the workbook to edit
 * @param {Array<{ref: string, value: string|number|null}>} edits
 * @returns {Promise<{buffer: ArrayBuffer, applied: number, skipped: string[], sheet: string}>}
 */
export const editWorkbookCells = async (file, edits = []) => {
  if (/\.xls$/i.test(file.name || "")) {
    throw new Error(
      (file.name || "This workbook") +
        " is in the legacy .xls format, which cannot be edited here. Open it in Excel and save it as .xlsx, then upload it again."
    );
  }

  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const { path, name } = await findSheetPath(zip);

  const sheetXml = await zip.file(path).async("string");
  const { xml, applied, skipped } = applyCellEdits(sheetXml, edits);

  zip.file(path, xml);

  const buffer = await zip.generateAsync({
    type: "arraybuffer",
    compression: "DEFLATE",
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  return { buffer, applied, skipped, sheet: name };
};
